import React, { useState } from 'react';

/**
 * A titled section that starts collapsed.
 *
 * Both entity-curation tools (Entity Merge, Group Membership) are tall, and they sit on
 * pages that already carry plenty — FieldHQ's Configuration tab and the Dachido Admin
 * entities tab. Neither is used on most visits, so they open on demand rather than
 * pushing everything else off the screen.
 *
 * Children are NOT rendered while collapsed, deliberately. Both tools fetch on mount
 * (entity lists, group lists), so rendering them behind a hidden div would fire requests
 * for a panel nobody has opened — on every page load, for every admin. Unmounting means
 * a closed section costs nothing. The trade is that collapsing and reopening refetches
 * and loses in-panel state; that is the right way round for tools used occasionally and
 * deliberately, and it also means reopening always shows current data rather than a
 * stale snapshot from earlier in the session.
 *
 * Props:
 *   title       — heading text, always visible
 *   subtitle    — optional one-liner shown under the title when open
 *   defaultOpen — start expanded (default false)
 *   right       — optional node rendered at the right of the header (e.g. a count)
 */
function CollapsibleSection({ title, subtitle, defaultOpen = false, right, children }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section style={{ marginBottom: '24px', border: '1px solid #e9ecef', borderRadius: '10px',
                      background: '#fff', overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
          padding: '14px 16px', background: open ? '#f8f9fa' : '#fff', border: 'none',
          borderBottom: open ? '1px solid #e9ecef' : 'none', cursor: 'pointer',
          textAlign: 'left', font: 'inherit',
        }}
      >
        <span style={{ color: '#868e96', fontSize: '12px', width: '12px', flexShrink: 0 }}>
          {open ? '▾' : '▸'}
        </span>
        <span style={{ fontSize: '16px', fontWeight: 600, color: '#1a1a1a', flex: 1 }}>
          {title}
        </span>
        {right}
      </button>
      {open && (
        <div style={{ padding: '16px' }}>
          {subtitle && (
            <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '14px' }}>
              {subtitle}
            </div>
          )}
          {children}
        </div>
      )}
    </section>
  );
}

export default CollapsibleSection;
